import { useCallback, useMemo, useRef } from 'react';
import { fetchTurnaroundAgent } from '@connect-x-app/hybrid-data-access';
import { tanstackMetadata } from '@tanstack/ai/client';
import { UIMessage, useChat } from '@tanstack/ai-react';
import { v4 as uuidv4 } from 'uuid';

import { useAppEnvConfig } from './feature';

export const isFailedAnswer = (message: UIMessage) => tanstackMetadata(message)?.stopReason === 'error';

export const useConnectedTurnaround = (agentName: string) => {
  const { appConfig, env } = useAppEnvConfig();
  const serviceUrl = env?.connectedTurnaroundServiceUrl;
  const agentRef = useRef({ agentName, serviceUrl: serviceUrl ?? '' });
  const threadId = useRef(uuidv4()).current;

  const forwardedProps = useMemo(
    () => ({
      client: {
        account: appConfig.account,
        activities: [],
        deviceId: appConfig.deviceId,
        name: appConfig.type.toLowerCase(),
        text: 'markdown',
        version: appConfig.version,
      },
    }),
    [appConfig],
  );

  const { isLoading, messages, reload, sendMessage, status, stop } = useChat({
    fetcher: (input, options) => fetchTurnaroundAgent(agentRef.current, input, options),
    forwardedProps,
    queue: 'interrupt',
    threadId,
  });

  const send = useCallback(
    (text: string) => {
      const question = text.trim();
      if (serviceUrl && question) {
        sendMessage(question).catch(() => undefined);
      }
    },
    [sendMessage, serviceUrl],
  );

  const retry = useCallback(() => {
    if (status === 'error') {
      reload();
    }
  }, [reload, status]);

  return { isRunning: isLoading, messages, retry, send, stop };
};
